import * as THREE from 'three';

import { Axial, axialToWorld } from '../map/HexCoordinates';
import { HexCellData, cornerElevation } from '../map/HexSurface';
import { CORNERS, HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { EDGE_MASK, HALF_WIDTHS } from '../map/RiverSurface';
import { ROAD_HALF_WIDTH } from '../map/RoadSurface';
import { PropModel, Props } from './PropModels';

/**
 * Trees and rocks standing on a chunk.
 *
 * Everything about a prop — which model, which way it faces, how big it is and
 * where inside its cell it stands — is derived from the **cell's coordinates**.
 * Nothing is drawn from a random number generator, which is what makes a wood
 * look the same on every visit, on every machine, and after every reload: the
 * map is the seed. A generator would give a forest that reshuffles itself
 * whenever a chunk is unloaded and fetched again, and a player who walked
 * around a distinctive tree would come back to find it gone.
 *
 * **Richness decides how many.** A cell carries one prop where the deposit is
 * thinnest and five where it is richest, so a rich wood is a thicket and a poor
 * one is a few trees — which is what the number on the cell means. Size is a
 * separate question and is answered by the coordinates: two trees of the same
 * richness are different trees, and a cell does not grow uniformly larger
 * timber for being richer.
 */

/**
 * How tall a prop stands, in world units, before its own variation. A hex is
 * 0,87 across.
 */
const TREE_HEIGHT = 1.2;
const ROCK_HEIGHT = 0.42;

/**
 * Jak mocno głaz kładzie się na pochyłości pola, od zera do jedynki.
 *
 * Nie na całość: kamień ustawiony dokładnie prostopadle do stromego zbocza
 * wygląda jak do niego przyklejony. Trzy czwarte drogi do normalnej czyta się
 * jak bryła, która **leży tak, jak leży ziemia**, i wciąż ma swój ciężar.
 * Drzewa tego nie dostają — rosną do góry, na jakim by zboczu nie stały.
 */
const ROCK_LEAN = 0.75;

/**
 * How wide a prop may get, as a share of a hex, by kind.
 *
 * Crowns in a wood grow into one another, and a forest of trees stopping
 * politely at their own cell boundary looks like an orchard — so trees are
 * allowed a little over a hex. Boulders get more: a rock two and a half times
 * the old size is meant to be a landmark, and pinning it back to a tree's
 * allowance would take the size away again by the back door.
 */
const TREE_WIDEST = 1.1;
const ROCK_WIDEST = 1.5;

/**
 * How much a prop's size varies with **where it stands**, either side of one.
 *
 * Nothing here comes from richness. Richness decides how many things are on a
 * cell; the coordinates decide what each of them looks like. Two knobs, two
 * jobs, and neither doing the other's work quietly.
 */
const SIZE_SPREAD = 0.3;

/** How many props a cell carries, from the poorest deposit to the richest. */
const FEWEST = 1;
const MOST = 5;

/**
 * How far from the centre props stand, as a share of the hex radius.
 *
 * A lone one keeps near the middle of its cell. A crowd spreads out and is laid
 * on a rough ring rather than scattered: five draws from the same distribution
 * put two of them on top of each other more often than not, and a pile reads as
 * one big prop rather than as five.
 */
const WANDER_ALONE = 0.42;
const WANDER_CROWD = 0.62;

/**
 * What is left of a prop standing on a cell a ribbon runs through.
 *
 * A river arm is a tenth of a hex across at its widest, so a full-sized prop
 * beside one reads as a prop **in** it — the bank is not wide enough to hold
 * something that big without the water disappearing behind it. Smaller stones
 * on a bank are also what a river actually leaves there.
 *
 * **A tree gives up less than a stone**, and the difference is what the two are
 * doing there. A boulder beside a stream is a boulder the water left, and it
 * looks like one at three fifths; a wood does not stop being a wood where a
 * stream runs through it, and a third off is as much as a tree can lose before
 * it reads as a sapling. The number is per kind for that reason, not because
 * one of them was tuned later.
 */
const RIVER_ROCK = 0.6;
const RIVER_TREE = 2 / 3;

/**
 * How far from the ribbon a prop keeps, in world units, beyond its own width.
 *
 * The ribbon is drawn on the ground and the prop stands on it, so touching is
 * enough to look wrong from a low camera.
 */
const RIBBON_CLEARANCE = 0.03;

/**
 * A cell's own number: the same coordinates always give the same one, and two
 * neighbouring cells give unrelated ones.
 *
 * The mixing is a splitmix step — the same one the world generator uses for its
 * spawn search, for the same reason: four lines, no dependency, and no version
 * history that could shift a forest sideways in a future release.
 */
function hash(q: number, r: number, salt: number): number {
  let value = (Math.imul(q, 0x8da6b343) ^ Math.imul(r, 0xd8163841) ^ Math.imul(salt, 0xcb1ab31f)) >>> 0;
  value = Math.imul(value ^ (value >>> 15), 0x2c1b3c6d) >>> 0;
  value = Math.imul(value ^ (value >>> 12), 0x297a2d39) >>> 0;
  return (value ^ (value >>> 15)) >>> 0;
}

/** The same number as a fraction of one. */
function unit(q: number, r: number, salt: number): number {
  return hash(q, r, salt) / 4294967296;
}

/**
 * Krawędzie, przez które przechodzi **cokolwiek wstęgowego**: rzeka, droga
 * albo jedno i drugie.
 *
 * Suma masek, a nie sama rzeka — bo z punktu widzenia drzewa nie ma różnicy,
 * co przez jego pole biegnie: i tak nie wolno mu tam stać. Droga położona przez
 * las rozsuwa więc rekwizyty dokładnie tak, jak od początku rozsuwała je rzeka,
 * i nie kosztowało to drugiej reguły — tylko przeczytania tej samej szerzej.
 */
function ribbonEdges(cell: HexCellData): number {
  return ((cell.river ?? 0) | (cell.road ?? 0)) & EDGE_MASK;
}

/**
 * Połowa szerokości tego, co przez pole przechodzi.
 *
 * **Szersza z dwóch, gdy przechodzą obie.** Ramiona jednego pola miały dotąd
 * jedną szerokość i cały rachunek na tym stoi; most — czyli jedyne miejsce,
 * gdzie spotykają się droga i rzeka — dostaje przez to odrobinę więcej luzu,
 * niż potrzebuje. Jest to tańsze niż szerokość liczona osobno dla każdego
 * ramienia, a różnica dotyczy pól, na których i tak stoi przeprawa.
 */
function ribbonHalfWidth(cell: HexCellData): number {
  const river = (cell.river ?? 0) & EDGE_MASK ? HALF_WIDTHS[((cell.river ?? 0) >> 6) & 0b11] : 0;
  const road = (cell.road ?? 0) & EDGE_MASK ? ROAD_HALF_WIDTH : 0;
  return Math.max(river, road);
}

/**
 * Which way the ribbon leaves this cell, as angles in the XZ plane.
 *
 * One per edge in the mask, pointing at the middle of that edge — which is
 * where the ribbon builder puts the arm's mouth, so these are the axes of the
 * ribbon rather than an approximation of it.
 */
function ribbonArms(cell: HexCellData): number[] {
  const edges = ribbonEdges(cell);
  if (edges === 0) return [];

  const arms: number[] = [];
  for (let direction = 0; direction < 6; direction++) {
    if ((edges & (1 << direction)) === 0) continue;

    const left = CORNERS[direction];
    const right = CORNERS[direction + 1];
    arms.push(Math.atan2((left.z + right.z) / 2, (left.x + right.x) / 2));
  }
  return arms;
}

/**
 * An angle in the gap between two arms of a ribbon, so a crowd ends up **on
 * both sides** rather than all on one.
 *
 * The arms cut the cell into as many wedges as there are of them, and props are
 * dealt into those wedges in turn: two arms make two banks, a confluence makes
 * three, and a source makes one wedge that wraps all the way round. `clear` is
 * the angle that has to be kept either side of an arm for the prop not to
 * overhang the water; where a wedge is too narrow to give even that, the prop
 * goes down its middle and is shrunk to fit — a narrow bank gets a small stone
 * rather than no stone, and that is the difference between a river with rocks
 * along it and a river with rocks on one side of it. Droga czyta się tą samą
 * regułą: las rozstępuje się **po obu stronach traktu**, a nie zbiera po jednej.
 */
function besideRibbon(
  arms: number[],
  slot: number,
  draw: number,
  clear: number
): number {
  const sorted = [...arms].sort((a, b) => a - b);

  const wedges = sorted.map((from, index) => {
    const next = sorted[(index + 1) % sorted.length];
    const to = index === sorted.length - 1 ? next + Math.PI * 2 : next;
    return { from, to };
  });

  const wedge = wedges[slot % wedges.length];
  const margin = Math.min(clear, (wedge.to - wedge.from) / 2 - 1e-3);
  const from = wedge.from + margin;
  const to = wedge.to - margin;

  return from + draw * (to - from);
}

/**
 * W którą stronę i jak mocno pochylona jest powierzchnia pola.
 *
 * Liczone z **tych samych narożników, z których zbudowany jest teren** — każdy
 * jest średnią z trzech pól — więc głaz kładzie się na powierzchni, którą widać,
 * a nie na wysokości środka pola. Suma wektorów do narożników, ważona różnicą
 * wysokości: narożnik wyżej ciągnie w swoją stronę, niżej — w przeciwną.
 */
function surfaceLean(
  world: Map<number, HexCellData>,
  cell: HexCellData
): THREE.Vector3 | null {
  const middle = elevationToY(cell.elevation);
  let x = 0;
  let z = 0;

  for (let corner = 0; corner < 6; corner += 1) {
    const height = elevationToY(cornerElevation(world, cell, corner)) - middle;
    x += CORNERS[corner].x * height;
    z += CORNERS[corner].z * height;
  }

  // Normalna płaszczyzny najlepiej pasującej do tych narożników: spadek w osiach
  // X i Z, promień heksa w pionie — czyli nachylenie mierzone tak, jak mierzy je
  // reszta tego projektu, w wysokości na krok.
  const lean = new THREE.Vector3(-x, HEX_SIZE, -z);
  return lean.lengthSq() > 1e-9 ? lean.normalize() : null;
}

/** How close a point inside a cell is to the ribbon's centre line. */
function toRibbon(cell: HexCellData, offsetX: number, offsetZ: number): number {
  const edges = ribbonEdges(cell);

  let closest = Infinity;
  for (let direction = 0; direction < 6; direction++) {
    if ((edges & (1 << direction)) === 0) continue;

    const left = CORNERS[direction];
    const right = CORNERS[direction + 1];
    const midX = (left.x + right.x) / 2;
    const midZ = (left.z + right.z) / 2;

    // Nearest point on the arm, which runs from the cell centre to that middle.
    const along = Math.max(
      0,
      Math.min(1, (offsetX * midX + offsetZ * midZ) / (midX * midX + midZ * midZ))
    );
    closest = Math.min(
      closest,
      Math.hypot(offsetX - midX * along, offsetZ - midZ * along)
    );
  }

  return closest;
}

/**
 * The smallest a boulder may be shrunk to fit a bank before it is dropped
 * instead, as a share of its full height.
 *
 * Below this it is a pebble seen from a camera that renders a hex 28 pixels
 * across — which is to say, not seen at all, and paid for anyway.
 */
const RIBBON_LEAST = 0.4;

export interface Placement {
  model: PropModel;
  matrix: THREE.Matrix4;
}

/**
 * How many props a deposit of this richness carries: one for the thinnest, five
 * for the richest.
 */
export function countFor(richness: number): number {
  const share = Math.min(1, Math.max(0, richness / 255));
  return FEWEST + Math.round(share * (MOST - FEWEST));
}

/**
 * One prop: which model, where on the cell, facing which way, how big.
 *
 * `index` is the prop's place in its cell's crowd and goes into every draw, so
 * the second tree on a cell is a different tree from the first rather than the
 * same one twice. Everything else comes from the coordinates, which is what
 * makes a wood the same wood on every visit.
 */
function place(
  cell: HexCellData,
  models: PropModel[],
  height: number,
  widest: number,
  index: number,
  count: number,
  salt: number,
  /**
   * Ile zostaje z rekwizytu na polu, przez które **coś przechodzi** — rzeka,
   * droga albo jedno i drugie — albo `undefined` dla rodzaju, który stoi, gdzie
   * chce, i nie ustępuje niczemu.
   *
   * The presence of a number is what turns the whole bank rule on, so a kind
   * cannot be given a size to shrink to and then forgotten about when the
   * angles are dealt out.
   */
  ribbonShrink?: number,
  /**
   * Świat i siła, z jaką rekwizyt kładzie się na pochyłości pola.
   *
   * **Dla głazów, nie dla drzew**: kamień leży tak, jak leży ziemia pod nim,
   * a drzewo rośnie do góry niezależnie od tego, na jakim stoi zboczu. Brak
   * i zero znaczą to samo — pion.
   */
  lean?: { world: Map<number, HexCellData>; strength: number }
): Placement | null {
  const seed = salt + index * 977;
  const model = models[hash(cell.q, cell.r, seed) % models.length];

  const { x, z } = axialToWorld(cell.q, cell.r);

  const arms = ribbonShrink === undefined ? [] : ribbonArms(cell);
  const onARibbon = arms.length > 0;
  const shrink = onARibbon ? (ribbonShrink ?? 1) : 1;

  // Size varies with the place, not with the deposit — and a bank is a smaller
  // place than open ground, so it is settled before the position: how much room
  // the prop needs is what decides where there is room for it.
  const wanted =
    height *
    (1 - SIZE_SPREAD + unit(cell.q, cell.r, seed + 4) * SIZE_SPREAD * 2) *
    shrink;

  // A squat model gets shorter rather than wider: whichever limit binds first.
  const allowed = (widest * HEX_SIZE * Math.sqrt(3)) / Math.max(model.footprint, 1e-6);
  const size = Math.min(wanted, allowed);

  // A crowd is laid out around a ring, each one nudged off its slot; a single
  // prop just wanders from the middle.
  const spread = count > 1 ? WANDER_CROWD : WANDER_ALONE;
  const drift =
    (count > 1 ? 0.45 + unit(cell.q, cell.r, seed + 2) * 0.55 : unit(cell.q, cell.r, seed + 2)) *
    spread *
    HEX_SIZE;

  // Clear of whatever runs through: the ribbon's half-width, the prop's own
  // half-width and a little air. Jedna liczba na pole — patrz `ribbonHalfWidth`.
  const bank = ribbonHalfWidth(cell) + (size * model.footprint) / 2 + RIBBON_CLEARANCE;

  // Standing off the middle is not optional on such a cell: the arms all meet
  // there, so no angle is clear until the prop has walked out past the join.
  const wander = onARibbon ? Math.max(drift, bank) : drift;

  const around = onARibbon
    ? besideRibbon(
        arms,
        index,
        unit(cell.q, cell.r, seed + 3),
        Math.asin(Math.min(1, bank / Math.max(wander, 1e-6)))
      )
    : count > 1
      ? ((index + unit(cell.q, cell.r, seed + 3) * 0.6) / count) * Math.PI * 2
      : unit(cell.q, cell.r, seed + 3) * Math.PI * 2;

  const offsetX = Math.cos(around) * wander;
  const offsetZ = Math.sin(around) * wander;

  // What the bank actually leaves, once the angle is settled: on a wide wedge
  // this changes nothing, and on a narrow one it is what keeps the stone out of
  // the water. Too little room and there is no stone here at all.
  const room = onARibbon
    ? toRibbon(cell, offsetX, offsetZ) - ribbonHalfWidth(cell) - RIBBON_CLEARANCE
    : Infinity;
  const fitted = Math.min(size, (Math.max(room, 0) * 2) / Math.max(model.footprint, 1e-6));
  if (onARibbon && fitted < height * shrink * RIBBON_LEAST) return null;

  const angle = unit(cell.q, cell.r, seed + 1) * Math.PI * 2;

  const turn = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), angle);

  // Pochylenie **przed** obrotem wokół pionu: najpierw kładziemy bryłę na
  // zboczu, potem obracamy ją na nim. Odwrotna kolejność obracałaby oś
  // pochylenia razem z bryłą, więc ten sam głaz na tym samym zboczu leżałby
  // inaczej w zależności od tego, jak wypadł mu obrót.
  const slope = lean && lean.strength > 0 ? surfaceLean(lean.world, cell) : null;
  if (slope && lean) {
    // Ułamek drogi do normalnej: na całość głaz kładłby się płasko na stromiźnie
    // i wyglądał jak do niej przyklejony.
    const wanted = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), slope);
    turn.premultiply(new THREE.Quaternion().slerp(wanted, lean.strength));
  }

  const matrix = new THREE.Matrix4().compose(
    new THREE.Vector3(x + offsetX, elevationToY(cell.elevation), z + offsetZ),
    turn,
    new THREE.Vector3(fitted, fitted, fitted)
  );

  return { model, matrix };
}

/**
 * Every prop on a chunk, as one group of instanced meshes.
 *
 * Returns null when the chunk carries nothing, so a caller can skip adding an
 * empty group — most chunks of open sea do.
 */
export function buildProps(
  members: HexCellData[],
  /**
   * Cały wczytany świat, nie tylko chunk.
   *
   * Potrzebny do pochylenia głazów: powierzchnia pola liczy się z narożników,
   * a te są średnią z **trzech** pól — więc pole na skraju chunka pyta o sąsiadów
   * spoza niego. Ta sama zależność, którą ma mesh terenu i z tego samego powodu.
   */
  world: Map<number, HexCellData>,
  props: Props,
  broadleafBiomes: ReadonlySet<number> = new Set(),
  /**
   * Cells something has been built on.
   *
   * A building stands **instead of** what grew there, not on top of it: the
   * trees are what the settler cleared to put it up, and a hut inside its own
   * thicket reads as two things occupying one cell. The predicate is passed in
   * rather than read from anywhere, because props are built per chunk and know
   * nothing about who owns what.
   */
  built: (cell: HexCellData) => boolean = () => false,
  /**
   * Cells whose trees are being cut. Their trees leave the batch and are drawn
   * on their own (`FellingTrees`), because one tree in an instanced batch
   * cannot shake or fall — the rocks on the same cell stay where they are.
   */
  felling: (cell: HexCellData) => boolean = () => false
): THREE.Group | null {
  const placements: Placement[] = [];

  for (const cell of members) {
    // Under water nothing stands: a lake covers its cell and the sea covers
    // rather more than that.
    if (cell.elevation < 0 || cell.lakeLevel !== undefined) continue;

    // Cleared: something was built here.
    if (built(cell)) continue;

    if (cell.forest && !felling(cell)) placements.push(...treesOn(cell, props, broadleafBiomes));

    if (cell.stone) {
      const count = countFor(cell.stone);
      for (let index = 0; index < count; index++) {
        // Null where a bank is too narrow to hold a rock at all: a cell a river
        // runs through carries fewer stones than its richness says, which is
        // what the river taking up the room means.
        const rock = place(
          cell,
          props.rocks,
          ROCK_HEIGHT,
          ROCK_WIDEST,
          index,
          count,
          0x510e,
          RIVER_ROCK,
          { world, strength: ROCK_LEAN }
        );
        if (rock) placements.push(rock);
      }
    }
  }

  if (placements.length === 0) return null;

  // Grouped by model, then one instanced mesh per part of it: bark and needles
  // are different materials and cannot share a draw call, but they can share
  // every transform.
  const byModel = new Map<PropModel, THREE.Matrix4[]>();
  for (const { model, matrix } of placements) {
    byModel.set(model, [...(byModel.get(model) ?? []), matrix]);
  }

  const group = new THREE.Group();
  group.name = 'props';

  for (const [model, matrices] of byModel) {
    for (const part of model.parts) {
      const mesh = new THREE.InstancedMesh(part.geometry, part.material, matrices.length);
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();

      group.add(mesh);
    }
  }

  return group;
}

/**
 * The trees one cell carries, exactly as the batch would place them.
 *
 * Shared by the batch and by `FellingTrees`, so a tree lifted out of the batch
 * to be cut down is the same tree in the same spot, not a lookalike.
 */
export function treesOn(
  cell: HexCellData,
  props: Props,
  broadleafBiomes: ReadonlySet<number> = new Set()
): Placement[] {
  if (!cell.forest) return [];
  // Which tree grows here is a fact about the biome, not about the cell's
  // coordinates — a pine in a rainforest is wrong however well it is
  // placed. Everything else about the prop still comes from the position.
  const broadleaf = cell.biome !== undefined && broadleafBiomes.has(cell.biome);
  const models = broadleaf && props.broadleaf.length > 0 ? props.broadleaf : props.pines;

  const trees: Placement[] = [];
  const count = countFor(cell.forest);
  for (let index = 0; index < count; index++) {
    // Null where a bank is too narrow for a tree, exactly as for a rock: a
    // cell a river or a road runs through carries fewer trees than its richness says,
    // because the water is taking up the room.
    const tree = place(cell, models, TREE_HEIGHT, TREE_WIDEST, index, count, 0x7d0e, RIVER_TREE);
    if (tree) trees.push(tree);
  }
  return trees;
}

/** How many triangles a built group of props actually costs. */
export function propTriangles(group: THREE.Group): number {
  let total = 0;
  for (const node of group.children) {
    if (!(node instanceof THREE.InstancedMesh)) continue;
    const index = node.geometry.getIndex();
    const count = index ? index.count : node.geometry.getAttribute('position').count;
    total += (count / 3) * node.count;
  }
  return total;
}

/** Cell coordinates a prop stands on, for tests and measurements. */
export function propsOn(members: HexCellData[]): Axial[] {
  return members
    .filter((cell) => cell.elevation >= 0 && cell.lakeLevel === undefined)
    .filter((cell) => cell.forest || cell.stone)
    .map((cell) => ({ q: cell.q, r: cell.r }));
}
